/**
 * Phone MY GAMES — the hub (DESIGN-CHANGELOG 2026-09-03 "MY GAMES: the page
 * about you" + "Phone: the five doors as an icon strip"; `design/GT Mobile
 * Mine.dc.html`, the `isMine` block). Phase 1 of docs/porting/AA.md.
 *
 * One stack: the doors as an ICON STRIP — one row of equal 58px squares, a
 * drawn glyph over a pixel label at the 5.5px floor, a count as a corner badge
 * — then RECOMMENDED TO YOU (phase 3: one stacked card per pending send, a
 * 44px button row; hidden when nothing is pending), then the NOTICE BOARD.
 * `Mobile Doors Options.dc.html` explored three
 * shapes for the strip and 1A shipped: the 2×2 card grid it replaced cost
 * ~172px before any content once a fifth door arrived. No profile card: the
 * header avatar is still the way into PROFILE.
 *
 * The strip renders however many doors `mineVM` lists (two in phase 1, five by
 * phase 4), each an equal column — so the row stays a row as doors land.
 */

import { CoverArt } from '../components/CoverArt.tsx';
import { DoorIcon } from '../components/DoorIcon.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { mineVM } from '../store/selectors.ts';
import type { NoticeRow, RecToYouCard } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/** `GT_LOGIC.BELL_SOLID` */
const BELL = 'M3 0h3v1H3zM2 1h5v2H2zM1 3h7v3H1zM0 6h9v1H0zM3 8h3v1H3z';

function NoticeLine({ l }: { l: NoticeRow }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(l.id + 'p', l.coverCands);
  return (
    <div
      onClick={() => openGame(l.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '11px 0',
        borderBottom: '1px solid var(--gt-line-0)',
        cursor: 'pointer',
        minHeight: 44,
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 30,
          height: 45,
          flex: 'none',
          background: l.fall,
          overflow: 'hidden',
          border: '1px solid var(--gt-line)',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={8} ini={l.ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontFamily: 'var(--gt-font-sans)',
              fontWeight: 600,
              fontSize: '12.5px',
              color: 'var(--gt-text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {l.n}
          </span>
          <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.06em', color: 'var(--gt-tick)', flex: 'none' }}>
            · {l.w}
          </span>
        </div>
        <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 14, lineHeight: 1.45, color: 'var(--gt-muted)', textWrap: 'pretty' }}>
          {l.t}
        </span>
      </div>
      <span
        style={{
          fontFamily: MONO,
          fontSize: '7.5px',
          letterSpacing: '.09em',
          color: l.tagClr,
          border: '1px ' + l.tagBs + ' ' + l.tagClr,
          background: l.tagBg,
          padding: '3px 5px',
          flex: 'none',
        }}
      >
        {l.tag}
      </span>
    </div>
  );
}

function RecCard({ c }: { c: RecToYouCard }): JSX.Element {
  const st = useQd();
  const art = useArt(c.gid + 'w', c.hdrCands);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid var(--gt-border)', background: 'var(--gt-card)', padding: 12 }}>
      <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
        <div
          onClick={() => st.openGame(c.gid)}
          style={{ position: 'relative', width: 104, height: 49, flex: 'none', background: c.fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}
        >
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={10} ini={c.ini} hasCover={art.hasCover} cover={art.cover} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <div
              style={{
                position: 'relative',
                width: 16,
                height: 16,
                flex: 'none',
                background: c.fromHueBg,
                display: 'grid',
                placeItems: 'center',
                fontFamily: PIXEL,
                fontSize: 5,
                color: 'var(--gt-ink)',
                overflow: 'hidden',
              }}
            >
              {c.fromIni}
              {c.fromHasAv ? <div style={{ position: 'absolute', inset: 0, background: c.fromAvBg }} /> : null}
            </div>
            <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.06em', color: 'var(--gt-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.by}</span>
          </div>
          <span onClick={() => st.openGame(c.gid)} style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: '13.5px', color: 'var(--gt-text)', cursor: 'pointer' }}>
            {c.n}
          </span>
        </div>
      </div>
      <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: '12.5px', lineHeight: 1.5, color: 'var(--gt-muted)', textWrap: 'pretty' }}>{c.note}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        <div
          onClick={() => void st.sendAdd(c.id)}
          style={{ flex: 1, height: 44, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 9.5, letterSpacing: '.07em', color: 'var(--acc)', border: '1px solid var(--acc)', background: 'var(--accSoft)', cursor: 'pointer', boxSizing: 'border-box' }}
        >
          ADD TO BACKLOG ▸
        </div>
        <div
          onClick={() => void st.sendSet(c.id, 'x')}
          style={{ flex: 1, height: 44, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 9.5, letterSpacing: '.07em', color: 'var(--gt-text-5)', border: '1px solid var(--gt-border-2)', cursor: 'pointer', boxSizing: 'border-box' }}
        >
          NOT FOR ME
        </div>
      </div>
    </div>
  );
}

export default function Mine(): JSX.Element {
  const s = useQd();
  const v = mineVM(s);

  // Both cover-bearing lists on this page fetch their own art (`useQueueArt`).
  // Neither population is on any other surface that asks: a RECOMMENDED TO YOU
  // card is a game somebody ELSE picked — routinely on neither the party list
  // nor your shelf nor the Discover head — and a notice-board row is whatever
  // the party scored or a trigger fired on. For a modern appid that means no
  // art at all, which is what Marcus saw on 2026-09-13.
  useQueueArt([...v.recs.map((c) => c.gid), ...v.nb.map((l) => l.id)]);

  return (
    <div data-screen-label="My games" style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '16px 14px 26px' }}>
      {/* the doors — an icon strip ------------------------------------------ */}
      {/* six cells since MY SCORES (2026-09-12): `repeat(6,1fr)`, 5px gap */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + v.doors.length + ', 1fr)', gap: 5 }}>
        {v.doors.map((d) => (
          <div
            key={d.key}
            onClick={() => s.nav(d.screen)}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              height: 58,
              border: '1px solid var(--gt-border)',
              background: 'var(--gt-card)',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <DoorIcon kind={d.icon} size={12.5} />
            <span style={{ fontFamily: PIXEL, fontSize: '5.5px', lineHeight: 1.6, color: 'var(--gt-text-4)', whiteSpace: 'nowrap' }}>
              {d.short}
            </span>
            {d.hasN ? (
              <span
                data-tip={d.tip}
                style={{
                  position: 'absolute',
                  top: -1,
                  right: -1,
                  minWidth: 16,
                  height: 16,
                  boxSizing: 'border-box',
                  padding: '0 4px',
                  background: 'var(--acc)',
                  color: 'var(--gt-ink)',
                  fontFamily: MONO,
                  fontSize: 9,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {d.n}
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {/* RECOMMENDED TO YOU — only what is still waiting on you ---------------- */}
      {v.hasRecs ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: PIXEL, fontSize: 9, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>RECOMMENDED TO YOU</span>
            <div style={{ flex: 1 }} />
            <span onClick={() => s.nav('recs')} style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--gt-text-5)', cursor: 'pointer', padding: '8px 0 8px 8px' }}>
              ALL ▸
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {v.recs.map((c) => (
              <RecCard key={c.key} c={c} />
            ))}
          </div>
        </div>
      ) : null}

      {/* the notice board -------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <svg viewBox="0 0 9 9" style={{ width: 13, height: 13, display: 'block', flex: 'none', color: 'var(--acc)' }} shapeRendering="crispEdges" aria-hidden="true">
            <path d={BELL} fill="currentColor" />
          </svg>
          <span style={{ fontFamily: PIXEL, fontSize: 9, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>NOTICE BOARD</span>
        </div>
        <div style={{ borderTop: '1px solid var(--gt-line)' }}>
          {v.nb.map((l) => (
            <NoticeLine key={l.key} l={l} />
          ))}
          {v.nbEmpty ? (
            <div style={{ padding: '22px 0', fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)' }}>
              NOTHING TO REPORT YET
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
